// News items now pin against the real Pin API (see bridge cross-repo contract,
// backend implemented in the sibling onelike-backend-agentic repo). Mirrors the
// fetch pattern already used by pin.service.js (the working Publisher-pin
// service): same authHeaderWithJSON() header helper, same fetch shape - but
// unlike pin.service.js this parses the JSON response itself, because
// newspin.actions.js (unchanged) expects the resolved value to already be
// { pinned, list } / { list }, not a raw Response.

import { authHeaderWithJSON } from "../helpers";

// Backend PinDTO declares @JsonIgnoreProperties(ignoreUnknown = true), so
// Jackson would silently drop any extra fields anyway - sending the full
// NewsPinClass instance (which also carries id, image, onlypinned, timeago)
// would NOT throw. This trims to exactly PinDTO's shape anyway, at the HTTP
// call site, purely to keep the wire payload minimal and self-documenting,
// rather than trimming NewsPinClass itself, which other code still relies on
// for its full shape.
function toPinDTO(pinObject) {
    const { url, title, source, pinned, publishedat, uuid, firstname, name, email, displayname } = pinObject;
    return { url, title, source, pinned, publishedat, uuid, firstname, name, email, displayname };
}

function pin(pinObject) {
    const body = JSON.stringify(toPinDTO(pinObject));

    return fetch(`${process.env.REACT_APP_ONELIKE_BACKEND_API}/pin`,
        {
            method: 'POST',
            headers: authHeaderWithJSON(),
            body: body
        })
        .then(res => res.json())
        // The backend only ever returns { pinned } - it never echoes the
        // pinned/unpinned item itself (see PinController#pinInfoNews in
        // onelike-backend-agentic), so build it here from the request
        // payload, which already carries every field savedNewsList's render
        // needs (url/title/source/publishedat/timeago). PinService#pin sets
        // the row to exactly the `pinned` value it was sent, so pinResult.pinned
        // is guaranteed to equal what this pinObject already asked for - no
        // separate GET is needed to know the resulting state.
        // newspin.reducer.js's NEWSPIN_SAVE_SUCCESS case applies this as a
        // local savedNewsList add/remove + pinCounts +1/-1 delta, instead of
        // gating the UI on a getSavedNewsList()/getPinCounts() re-fetch after
        // every single pin/unpin (those two functions are still used as-is
        // for the one-time mount-time fetches in Home.js/Switch.js).
        .then(pinResult => ({
            pinned: pinResult.pinned,
            item: {
                url: pinObject.url,
                title: pinObject.title,
                source: pinObject.source,
                publishedat: pinObject.publishedat,
                timeago: pinObject.timeago,
                pinned: pinResult.pinned
            }
        }));
}

function getSavedNewsList(uuid) {
    return fetch(`${process.env.REACT_APP_ONELIKE_BACKEND_API}/mypinnednews?uuid=${uuid}`,
        {
            method: 'GET',
            headers: authHeaderWithJSON()
        })
        .then(res => res.json())
        .then(list => ({ list: list }));
}

// Public, not user-scoped: how many users have pinned each article. Fetched
// once on load (not per rendered item - see NewsSaveButton) and refreshed
// after every pin/unpin.
function getPinCounts() {
    return fetch(`${process.env.REACT_APP_ONELIKE_BACKEND_API}/pincount`,
        {
            method: 'GET',
            headers: authHeaderWithJSON()
        })
        .then(res => res.json())
        .then(counts => ({ counts: counts }));
}

export const newspinService = {
    pin,
    getSavedNewsList,
    getPinCounts
};
